import React, { useEffect, useMemo, useState } from "react";
import {
  CBadge,
  CButton,
  CCallout,
  CFormCheck,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormTextarea,
  CLoadingButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CSmartTable,
  CStepper,
} from "@coreui/react-pro";
import EmployerPageFrame from "../../components/employer/EmployerPageFrame";
import useEmployerOsWorkspace from "../../hooks/useEmployerOsWorkspace";
import { buildGateSummary } from "../../services/employer/employerCaseOsDomain";
import { getStoredIdentity } from "../../utils/authSession";
import {
  buildEmployerTrustBlockerRows,
  buildEmployerTrustEvidenceRows,
  buildEmployerTrustHistoryRows,
  buildEmployerTrustIncidentRows,
  buildEmployerTrustOpsSummary,
  buildEmployerTrustReviewRows,
  buildEmployerTrustSignalRows,
  buildEmployerTrustSummary,
  buildEmployerTrustTaskRows,
  formatEmployerTypeLabel,
  formatTrustIncidentTypeLabel,
  getEmployerTrustStatusColor,
  getPublishingRightsStageColor,
  getRiskLevelColor,
  getTrustTierColor,
  getTrustIncidentStatusColor,
  getTrustReviewWorkflowStatusColor,
  getTrustTaskStatusColor,
} from "../../services/employer/employerTrustDomain";

const housingOptions = [
  { label: "Bitte waehlen", value: "" },
  { label: "Hilfe bei der Unterkunftssuche", value: "hilfe_bei_suche" },
  { label: "Unterkunft wird gestellt", value: "unterkunft_wird_gestellt" },
  { label: "Partnernetzwerk / organisierte Wohnungsloesung", value: "partnernetzwerk" },
];

const employerTypeOptions = [
  { label: "Bitte waehlen", value: "unknown_unverified" },
  { label: "Direktarbeitgeber", value: "direct_employer" },
  { label: "Staffing Agency", value: "staffing_agency" },
  { label: "Recruiter / Vermittler", value: "recruiter_intermediary" },
  { label: "Consultant / Dritter", value: "consultant_third_party" },
  { label: "Abgelehnt wegen Falschdarstellung", value: "rejected_misrepresentation" },
];

const facilityTypeOptions = [
  { label: "Bitte waehlen", value: "other" },
  { label: "Krankenhaus", value: "hospital" },
  { label: "Klinik", value: "clinic" },
  { label: "Pflegeeinrichtung", value: "care_facility" },
  { label: "Pflegeheim", value: "nursing_home" },
  { label: "Praxis", value: "practice" },
  { label: "Reha-Zentrum", value: "rehab_center" },
  { label: "Sonstige Einrichtung", value: "other" },
];

const trustMethodOptions = [
  { label: "Self reported", value: "self_reported" },
  { label: "Manual Review", value: "manual_review" },
  { label: "Domain Check", value: "domain_check" },
  { label: "Public Presence Check", value: "public_presence_check" },
  { label: "Complaint Recheck", value: "complaint_recheck" },
];

const verificationOptions = [
  { label: "Pending", value: "pending" },
  { label: "Verified", value: "verified" },
  { label: "Rejected", value: "rejected" },
  { label: "Mismatch", value: "mismatch" },
  { label: "Free Mail", value: "free_mail" },
  { label: "Missing", value: "missing" },
  { label: "Unchecked", value: "unchecked" },
];

const firstAdReviewOptions = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Failed", value: "failed" },
];

const reviewWorkflowOptions = [
  { label: "Review nicht gestartet", value: "review_not_started" },
  { label: "Review in Pruefung", value: "review_in_progress" },
  { label: "Wartet auf Employer", value: "review_waiting_for_employer" },
  { label: "Wartet auf Nachweise", value: "review_waiting_for_evidence" },
  { label: "Review verifiziert", value: "review_completed_verified" },
  { label: "Review abgelehnt", value: "review_completed_rejected" },
];

const trustTaskStatusOptions = [
  { label: "Offen", value: "open" },
  { label: "In Pruefung", value: "in_progress" },
  { label: "Erledigt", value: "completed" },
  { label: "Abgelehnt", value: "rejected" },
  { label: "Follow-up noetig", value: "requires_followup" },
];

const incidentTypeOptions = [
  { label: "Fake Job", value: "fake_job" },
  { label: "Verdeckter Vermittler", value: "hidden_intermediary" },
  { label: "Falscher Arbeitgeber-Claim", value: "wrong_employer_claim" },
  { label: "Provision / Zahlung verlangt", value: "payment_requested" },
  { label: "Falsches Unterkunftsversprechen", value: "false_promise_housing" },
  { label: "Interview-Mismatch", value: "interview_mismatch" },
  { label: "Sonstiger Incident", value: "other" },
];

const incidentSeverityOptions = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
  { label: "Critical", value: "critical" },
];

const incidentStatusOptions = [
  { label: "Offen", value: "open" },
  { label: "Unter Pruefung", value: "under_review" },
  { label: "Bestaetigt", value: "confirmed" },
  { label: "Verworfen", value: "rejected" },
  { label: "Geloest", value: "resolved" },
];

const emptyForm = {
  employer_type: "unknown_unverified",
  legal_entity_name: "",
  facility_name: "",
  represented_facility_name: "",
  represented_facility_proof: "",
  facility_type: "other",
  official_website: "",
  official_domain_email: "",
  official_phone: "",
  registered_address: "",
  country: "",
  city: "",
  contact_person_name: "",
  contact_person_role: "",
  publishing_authority_proof: "",
  legal_entity_proof: "",
  facility_proof: "",
  trust_verification_method: "self_reported",
  domain_verification_status: "unchecked",
  publishing_authority_status: "pending",
  legal_entity_verification_status: "pending",
  facility_verification_status: "pending",
  first_ad_review_status: "pending",
  trust_review_notes: "",
  complaint_count: 0,
  active_complaint_flag: false,
  trust_recheck_required: false,
  publishing_hold: false,
  misrepresentation_flag: false,
  housing_support_confirmed: false,
  housing_support_mode: "",
  onboarding_bonus_min: 1500,
  first_salary_bridge_plan: "",
  integration_owner_name: "",
  integration_owner_email: "",
  team_preparedness_note: "",
  salary_transparency_note: "",
  arrival_support_note: "",
  accommodation_note: "",
  payroll_bridge_ready: false,
  team_ready: false,
  compensation_transparent: false,
};

export default function EmployerProfilePage() {
  const workspace = useEmployerOsWorkspace();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showTrustDetails, setShowTrustDetails] = useState(false);
  const [saving, setSaving] = useState(false);
  const [opsSaving, setOpsSaving] = useState(false);
  const [realityChecking, setRealityChecking] = useState(false);
  const [reviewDraft, setReviewDraft] = useState(null);
  const [taskDraft, setTaskDraft] = useState(null);
  const [incidentDraft, setIncidentDraft] = useState(null);
  const isReviewer = String(getStoredIdentity().role || "").toLowerCase() === "admin";

  useEffect(() => {
    if (!workspace.profile) return;
    setForm({
      employer_type: workspace.profile.employer_type || "unknown_unverified",
      legal_entity_name: workspace.profile.legal_entity_name || "",
      facility_name: workspace.profile.facility_name || "",
      represented_facility_name: workspace.profile.represented_facility_name || "",
      represented_facility_proof: workspace.profile.represented_facility_proof || "",
      facility_type: workspace.profile.facility_type || "other",
      official_website: workspace.profile.official_website || "",
      official_domain_email: workspace.profile.official_domain_email || "",
      official_phone: workspace.profile.official_phone || "",
      registered_address: workspace.profile.registered_address || "",
      country: workspace.profile.country || "",
      city: workspace.profile.city || "",
      contact_person_name: workspace.profile.contact_person_name || "",
      contact_person_role: workspace.profile.contact_person_role || "",
      publishing_authority_proof: workspace.profile.publishing_authority_proof || "",
      legal_entity_proof: workspace.profile.legal_entity_proof || "",
      facility_proof: workspace.profile.facility_proof || "",
      trust_verification_method: workspace.profile.trust_verification_method || "self_reported",
      domain_verification_status: workspace.profile.domain_verification_status || "unchecked",
      publishing_authority_status: workspace.profile.publishing_authority_status || "pending",
      legal_entity_verification_status: workspace.profile.legal_entity_verification_status || "pending",
      facility_verification_status: workspace.profile.facility_verification_status || "pending",
      first_ad_review_status: workspace.profile.first_ad_review_status || "pending",
      trust_review_notes: workspace.profile.trust_review_notes || "",
      complaint_count: Number(workspace.profile.complaint_count || 0),
      active_complaint_flag: Boolean(workspace.profile.active_complaint_flag),
      trust_recheck_required: Boolean(workspace.profile.trust_recheck_required),
      publishing_hold: Boolean(workspace.profile.publishing_hold),
      misrepresentation_flag: Boolean(workspace.profile.misrepresentation_flag),
      housing_support_confirmed: Boolean(workspace.profile.housing_support_confirmed),
      housing_support_mode: workspace.profile.housing_support_mode || "",
      onboarding_bonus_min: Number(workspace.profile.onboarding_bonus_min || 1500),
      first_salary_bridge_plan: workspace.profile.first_salary_bridge_plan || "",
      integration_owner_name: workspace.profile.integration_owner_name || "",
      integration_owner_email: workspace.profile.integration_owner_email || "",
      team_preparedness_note: workspace.profile.team_preparedness_note || "",
      salary_transparency_note: workspace.profile.salary_transparency_note || "",
      arrival_support_note: workspace.profile.arrival_support_note || "",
      accommodation_note: workspace.profile.accommodation_note || "",
      payroll_bridge_ready: Boolean(workspace.profile.payroll_bridge_ready),
      team_ready: Boolean(workspace.profile.team_ready),
      compensation_transparent: Boolean(workspace.profile.compensation_transparent),
    });
  }, [workspace.profile]);

  const composedProfile = useMemo(() => ({ ...(workspace.profile || {}), ...form }), [workspace.profile, form]);
  const gateSummary = useMemo(() => buildGateSummary(form), [form]);
  const trustSummary = useMemo(() => buildEmployerTrustSummary(composedProfile), [composedProfile]);
  const trustOpsSummary = useMemo(() => buildEmployerTrustOpsSummary(workspace.profile || {}), [workspace.profile]);
  const trustSignalRows = useMemo(() => buildEmployerTrustSignalRows(composedProfile), [composedProfile]);
  const trustBlockerRows = useMemo(() => buildEmployerTrustBlockerRows(composedProfile), [composedProfile]);
  const trustReviewRows = useMemo(() => buildEmployerTrustReviewRows(workspace.profile || {}), [workspace.profile]);
  const trustTaskRows = useMemo(() => buildEmployerTrustTaskRows(workspace.profile || {}), [workspace.profile]);
  const trustIncidentRows = useMemo(() => buildEmployerTrustIncidentRows(workspace.profile || {}), [workspace.profile]);
  const trustHistoryRows = useMemo(() => buildEmployerTrustHistoryRows(workspace.profile || {}), [workspace.profile]);
  const trustEvidenceRows = useMemo(() => buildEmployerTrustEvidenceRows(workspace.profile || {}), [workspace.profile]);

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const formatStamp = (value) => {
    if (!value) return "-";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "-";
    return parsed.toLocaleString("de-DE");
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      await workspace.persistProfile(form);
      setShowConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  const saveReviewArea = async () => {
    if (!reviewDraft?.areaKey) return;
    try {
      setOpsSaving(true);
      await workspace.persistTrustReviewArea(reviewDraft.areaKey, {
        review_status: reviewDraft.reviewStatus,
        review_notes: reviewDraft.reviewNotes,
        reason: reviewDraft.reason || "Trust review area updated",
      });
      setReviewDraft(null);
    } finally {
      setOpsSaving(false);
    }
  };

  const saveReviewTask = async () => {
    if (!taskDraft?.id) return;
    try {
      setOpsSaving(true);
      await workspace.persistTrustReviewTask(taskDraft.id, {
        status: taskDraft.status,
        task_notes: taskDraft.notes,
        next_action: taskDraft.nextAction,
        is_blocking: taskDraft.isBlocking,
        reason: taskDraft.reason || "Trust review task updated",
      });
      setTaskDraft(null);
    } finally {
      setOpsSaving(false);
    }
  };

  const saveIncident = async () => {
    if (!incidentDraft?.type) return;
    try {
      setOpsSaving(true);
      await workspace.persistTrustIncident(incidentDraft.id || null, {
        incident_type: incidentDraft.type,
        severity: incidentDraft.severity,
        status: incidentDraft.status,
        summary: incidentDraft.summary,
        action_taken: incidentDraft.actionTaken,
        reported_by: incidentDraft.reportedBy,
        reason: incidentDraft.reason || "Trust incident updated",
      });
      setIncidentDraft(null);
    } finally {
      setOpsSaving(false);
    }
  };

  const runRealityCheck = async () => {
    if (!isReviewer) return;
    try {
      setRealityChecking(true);
      await workspace.persistTrustRealityCheck({
        ad_id: workspace.selectedAd?.id || workspace.ads?.[0]?.id || undefined,
        reason: "Manual employer reality check from trust profile",
      });
    } finally {
      setRealityChecking(false);
    }
  };

  return (
    <EmployerPageFrame
      title="Arbeitgeberprofil"
      subtitle="Das Arbeitgeberprofil fuehrt jetzt den adaptiven Employer Trust Gate: Trust Tier, Risk Level, Review-Tasks, Incidents und GPT-gestuetzten Reality Check gegen Tarnung, Fake-Claims und unklare Live-Quellen."
      actions={(
        <>
          <CButton color="secondary" variant="outline" onClick={() => workspace.loadProfile()}>
            Neu laden
          </CButton>
          {isReviewer ? (
            <CLoadingButton color="secondary" variant="outline" loading={realityChecking} onClick={runRealityCheck}>
              Reality Check
            </CLoadingButton>
          ) : null}
          <CButton color="secondary" variant="outline" onClick={() => setShowTrustDetails(true)}>
            Trust-Details
          </CButton>
          <CButton color="primary" onClick={() => setShowConfirm(true)}>
            Profil speichern
          </CButton>
        </>
      )}
      contentClassName="space-y-4"
      shellProps={{
        shellLabel: "Employer Trust Gate",
        shellDescription: "Direktarbeitgeber, Service Provider, adaptive Evidence Packs, Risk Tiers und GPT-Reality-Check laufen jetzt im selben Employer OS statt als weiche Zusatznotiz.",
        topContext: "Arbeitgeberprofil",
      }}
    >
      {workspace.error ? (
        <CCallout color="danger">
          <strong>Arbeitgeberprofil konnte nicht vollstaendig geladen werden.</strong>
          <div>{workspace.error}</div>
        </CCallout>
      ) : null}

      {!isReviewer ? (
        <CCallout color="warning">
          <strong>Trust-Review bleibt intern.</strong>
          <div>Du kannst Nachweise, Kontakt- und Einrichtungsdaten pflegen. Review-Status, Holds und Misrepresentation-Sperren bleiben schreibgeschuetzt, bis ein interner Review erfolgt.</div>
        </CCallout>
      ) : null}

      <section className="dashboard-card-shell space-y-4">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
          <div>
            <span className="dashboard-kicker">Employer Trust Gate</span>
            <h2 className="dashboard-card-title">Keine Live-Quelle ohne belastbare Arbeitgeberverifikation</h2>
            <p className="dashboard-section-copy">Dieses Profil verbindet Rechtstraeger, Einrichtung, Kontaktidentitaet, Publikationsbefugnis, First-Ad-Review und operative Arrival-Gates in derselben Product-Grammatik.</p>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="dashboard-summary-label">Trust-Status</span>
              <strong>{trustSummary.trustStatusLabel}</strong>
              <p className="mb-0 text-sm text-slate-600">{trustSummary.nextTrustAction}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="dashboard-summary-label">Employer-Typ</span>
              <strong>{trustSummary.employerTypeLabel}</strong>
              <p className="mb-0 text-sm text-slate-600">
                {trustSummary.canPublishLive ? "Live-Publishing ist derzeit moeglich." : `${trustSummary.publishBlockers.length} Publishing-Blocker aktiv.`}
              </p>
            </div>
          </div>
        </div>

        <CStepper
          activeStepNumber={step}
          layout="horizontal"
          onStepChange={setStep}
          steps={[
            { label: "Rechtstraeger" },
            { label: "Kontakt" },
            { label: "Nachweise" },
            { label: "Gate" },
            { label: "Bridge & Owner" },
            { label: "Team & Transparenz" },
            { label: "Trust Review" },
          ]}
        />

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(340px,0.8fr)]">
          <div className="space-y-4">
            {step === 1 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <CFormLabel>Employer-Typ</CFormLabel>
                    <CFormSelect options={employerTypeOptions} value={form.employer_type} onChange={(event) => updateField("employer_type", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Einrichtungsart</CFormLabel>
                    <CFormSelect options={facilityTypeOptions} value={form.facility_type} onChange={(event) => updateField("facility_type", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Rechtstraeger</CFormLabel>
                    <CFormInput value={form.legal_entity_name} onChange={(event) => updateField("legal_entity_name", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Einrichtung / Haus</CFormLabel>
                    <CFormInput value={form.facility_name} onChange={(event) => updateField("facility_name", event.target.value)} />
                  </div>
                  {form.employer_type !== "direct_employer" ? (
                    <div className="md:col-span-2">
                      <CFormLabel>Vertretene Einrichtung</CFormLabel>
                      <CFormInput value={form.represented_facility_name} onChange={(event) => updateField("represented_facility_name", event.target.value)} />
                    </div>
                  ) : null}
                  <div className="md:col-span-2">
                    <CFormLabel>Registrierte Adresse</CFormLabel>
                    <CFormInput value={form.registered_address} onChange={(event) => updateField("registered_address", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Land</CFormLabel>
                    <CFormInput value={form.country} onChange={(event) => updateField("country", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Stadt</CFormLabel>
                    <CFormInput value={form.city} onChange={(event) => updateField("city", event.target.value)} />
                  </div>
                </div>
              </div>
            ) : null}

            {step === 2 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <CFormLabel>Offizielle Website</CFormLabel>
                    <CFormInput value={form.official_website} onChange={(event) => updateField("official_website", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Offizielle Domain-E-Mail</CFormLabel>
                    <CFormInput type="email" value={form.official_domain_email} onChange={(event) => updateField("official_domain_email", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Offizielle Telefonnummer</CFormLabel>
                    <CFormInput value={form.official_phone} onChange={(event) => updateField("official_phone", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Ansprechpartner</CFormLabel>
                    <CFormInput value={form.contact_person_name} onChange={(event) => updateField("contact_person_name", event.target.value)} />
                  </div>
                  <div className="md:col-span-2">
                    <CFormLabel>Rolle der handelnden Person</CFormLabel>
                    <CFormInput value={form.contact_person_role} onChange={(event) => updateField("contact_person_role", event.target.value)} />
                  </div>
                </div>
              </div>
            ) : null}

            {step === 3 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="grid gap-3">
                  <div>
                    <CFormLabel>Nachweis Rechtstraeger</CFormLabel>
                    <CFormTextarea rows={4} value={form.legal_entity_proof} onChange={(event) => updateField("legal_entity_proof", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Nachweis Einrichtung</CFormLabel>
                    <CFormTextarea rows={4} value={form.facility_proof} onChange={(event) => updateField("facility_proof", event.target.value)} />
                  </div>
                  {form.employer_type !== "direct_employer" ? (
                    <div>
                      <CFormLabel>Nachweis der Beauftragung</CFormLabel>
                      <CFormTextarea rows={4} value={form.represented_facility_proof} onChange={(event) => updateField("represented_facility_proof", event.target.value)} />
                    </div>
                  ) : null}
                  <div>
                    <CFormLabel>Nachweis Veroeffentlichungsbefugnis</CFormLabel>
                    <CFormTextarea rows={4} value={form.publishing_authority_proof} onChange={(event) => updateField("publishing_authority_proof", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Trust-Verifikationsmethode</CFormLabel>
                    <CFormSelect options={trustMethodOptions} value={form.trust_verification_method} onChange={(event) => updateField("trust_verification_method", event.target.value)} />
                  </div>
                </div>
              </div>
            ) : null}

            {step === 4 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3">
                  <CFormCheck
                    checked={form.housing_support_confirmed}
                    id="housing_support_confirmed"
                    label="Wohnungsunterstuetzung ist fuer internationale Fachkraefte verbindlich organisiert"
                    onChange={(event) => updateField("housing_support_confirmed", event.target.checked)}
                  />
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <CFormLabel>Modell der Wohnungsunterstuetzung</CFormLabel>
                    <CFormSelect options={housingOptions} value={form.housing_support_mode} onChange={(event) => updateField("housing_support_mode", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Willkommensbonus / Startunterstuetzung (EUR)</CFormLabel>
                    <CFormInput min={1500} type="number" value={form.onboarding_bonus_min} onChange={(event) => updateField("onboarding_bonus_min", Math.max(1500, Number(event.target.value || 1500)))} />
                  </div>
                </div>
                <div className="mt-3">
                  <CFormLabel>Unterkunfts- und Arrival-Notiz</CFormLabel>
                  <CFormTextarea rows={5} value={form.accommodation_note} onChange={(event) => updateField("accommodation_note", event.target.value)} />
                </div>
              </div>
            ) : null}

            {step === 5 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <CFormCheck
                  checked={form.payroll_bridge_ready}
                  id="payroll_bridge_ready"
                  label="Plan zur Ueberbrueckung bis zum ersten Gehalt ist definiert"
                  onChange={(event) => updateField("payroll_bridge_ready", event.target.checked)}
                />
                <div className="mt-3">
                  <CFormLabel>Gehaltsbruecken- / Liquiditaetsplan</CFormLabel>
                  <CFormTextarea rows={5} value={form.first_salary_bridge_plan} onChange={(event) => updateField("first_salary_bridge_plan", event.target.value)} />
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div>
                    <CFormLabel>Integration Owner</CFormLabel>
                    <CFormInput value={form.integration_owner_name} onChange={(event) => updateField("integration_owner_name", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>E-Mail / Kontakt</CFormLabel>
                    <CFormInput type="email" value={form.integration_owner_email} onChange={(event) => updateField("integration_owner_email", event.target.value)} />
                  </div>
                </div>
                <div className="mt-3">
                  <CFormLabel>Arrival- und Eskalationslogik</CFormLabel>
                  <CFormTextarea rows={5} value={form.arrival_support_note} onChange={(event) => updateField("arrival_support_note", event.target.value)} />
                </div>
              </div>
            ) : null}

            {step === 6 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <CFormCheck
                  checked={form.team_ready}
                  id="team_ready"
                  label="Team-/Stationsvorbereitung fuer Arrival und Einarbeitung ist organisiert"
                  onChange={(event) => updateField("team_ready", event.target.checked)}
                />
                <div className="mt-3">
                  <CFormLabel>Team- und Stationsvorbereitung</CFormLabel>
                  <CFormTextarea rows={5} value={form.team_preparedness_note} onChange={(event) => updateField("team_preparedness_note", event.target.value)} />
                </div>
                <div className="mt-3">
                  <CFormCheck
                    checked={form.compensation_transparent}
                    id="compensation_transparent"
                    label="Gehalts-, Einsatz- und Erwartungslogik ist transparent dokumentiert"
                    onChange={(event) => updateField("compensation_transparent", event.target.checked)}
                  />
                </div>
                <div className="mt-3">
                  <CFormLabel>Transparente Verguetungs- und Einsatzlogik</CFormLabel>
                  <CFormTextarea rows={5} value={form.salary_transparency_note} onChange={(event) => updateField("salary_transparency_note", event.target.value)} />
                </div>
              </div>
            ) : null}

            {step === 7 ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <CFormLabel>Domain Verification</CFormLabel>
                    <CFormSelect disabled={!isReviewer} options={verificationOptions} value={form.domain_verification_status} onChange={(event) => updateField("domain_verification_status", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Publishing Authority</CFormLabel>
                    <CFormSelect disabled={!isReviewer} options={verificationOptions} value={form.publishing_authority_status} onChange={(event) => updateField("publishing_authority_status", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Legal Entity Verification</CFormLabel>
                    <CFormSelect disabled={!isReviewer} options={verificationOptions} value={form.legal_entity_verification_status} onChange={(event) => updateField("legal_entity_verification_status", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Facility Verification</CFormLabel>
                    <CFormSelect disabled={!isReviewer} options={verificationOptions} value={form.facility_verification_status} onChange={(event) => updateField("facility_verification_status", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>First Ad Review</CFormLabel>
                    <CFormSelect disabled={!isReviewer} options={firstAdReviewOptions} value={form.first_ad_review_status} onChange={(event) => updateField("first_ad_review_status", event.target.value)} />
                  </div>
                  <div>
                    <CFormLabel>Complaint Count</CFormLabel>
                    <CFormInput disabled={!isReviewer} type="number" min={0} value={form.complaint_count} onChange={(event) => updateField("complaint_count", Math.max(0, Number(event.target.value || 0)))} />
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <CFormCheck disabled={!isReviewer} checked={form.active_complaint_flag} label="Active Complaint Flag" onChange={(event) => updateField("active_complaint_flag", event.target.checked)} />
                  <CFormCheck disabled={!isReviewer} checked={form.trust_recheck_required} label="Trust Recheck Required" onChange={(event) => updateField("trust_recheck_required", event.target.checked)} />
                  <CFormCheck disabled={!isReviewer} checked={form.publishing_hold} label="Publishing Hold" onChange={(event) => updateField("publishing_hold", event.target.checked)} />
                  <CFormCheck disabled={!isReviewer} checked={form.misrepresentation_flag} label="Misrepresentation Flag" onChange={(event) => updateField("misrepresentation_flag", event.target.checked)} />
                </div>
                <div className="mt-3">
                  <CFormLabel>Trust Review Notes</CFormLabel>
                  <CFormTextarea disabled={!isReviewer} rows={6} value={form.trust_review_notes} onChange={(event) => updateField("trust_review_notes", event.target.value)} />
                </div>
              </div>
            ) : null}
          </div>

          <aside className="space-y-4">
            <CCallout color={getEmployerTrustStatusColor(trustSummary.trustStatus)}>
              <div className="d-flex justify-content-between gap-3">
                <div>
                  <strong>{trustSummary.trustStatusLabel}</strong>
                  <div className="text-sm">{trustSummary.nextTrustAction}</div>
                </div>
                <CBadge color={getEmployerTrustStatusColor(trustSummary.trustStatus)}>{formatEmployerTypeLabel(trustSummary.employerType)}</CBadge>
              </div>
            </CCallout>

            <CCallout color={gateSummary.status === "complete" ? "success" : gateSummary.status === "review" ? "warning" : "danger"}>
              <strong>{gateSummary.statusLabel}</strong>
              <div>{gateSummary.readyCount} von {gateSummary.totalCount} operativen Employer-Gate-Bereichen sind belastbar dokumentiert.</div>
            </CCallout>

            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <span className="dashboard-summary-label">Publish Gate</span>
              <strong>{trustSummary.canPublishLive ? "Live-Publishing frei" : "Live-Publishing blockiert"}</strong>
              <p className="mb-0 mt-2 text-sm text-slate-600">{trustSummary.publishBlockers[0] || "Kein aktiver Trust-Blocker."}</p>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <span className="dashboard-summary-label">Trust Operations</span>
              <strong>{trustOpsSummary.reviewWorkflowLabel}</strong>
              <p className="mb-0 mt-2 text-sm text-slate-600">
                {trustOpsSummary.blockingTasks} blockierende Tasks · {trustOpsSummary.activeIncidents} aktive Incidents · Risiko {trustOpsSummary.riskLevel}
              </p>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <span className="dashboard-summary-label">Trust Tier / Risiko / Rechte</span>
              <div className="mt-2 d-flex flex-wrap gap-2">
                <CBadge color={getTrustTierColor(trustOpsSummary.trustTier)}>{trustOpsSummary.trustTierLabel}</CBadge>
                <CBadge color={getRiskLevelColor(trustOpsSummary.riskLevel)}>{trustOpsSummary.riskLevelLabel}</CBadge>
                <CBadge color={getPublishingRightsStageColor(trustOpsSummary.publishingRightsStage)}>{trustOpsSummary.publishingRightsStageLabel}</CBadge>
              </div>
              <p className="mb-0 mt-2 text-sm text-slate-600">
                Typklasse: {trustOpsSummary.employerClassificationLabel} · Behavior Risk {(trustOpsSummary.behaviorRiskScore * 100).toFixed(0)}%
              </p>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <span className="dashboard-summary-label">GPT Reality Check</span>
              <p className="mb-0 mt-2 text-sm text-slate-700">
                {trustOpsSummary.realityCheck.summary || "Noch kein externer Reality Check ausgefuehrt."}
              </p>
              <p className="mb-0 mt-2 text-sm text-slate-600">
                Plausibilitaet {(Number(trustOpsSummary.realityCheck.plausibilityScore || 0) * 100).toFixed(0)}% · Risiko {(Number(trustOpsSummary.realityCheck.riskScore || 0) * 100).toFixed(0)}% · letzter Lauf {formatStamp(trustOpsSummary.realityCheck.lastCheckedAt)}
              </p>
              {trustOpsSummary.realityCheck.flags?.length ? (
                <div className="mt-2 d-flex flex-wrap gap-2">
                  {trustOpsSummary.realityCheck.flags.slice(0, 6).map((entry) => (
                    <CBadge key={entry} color="warning" shape="rounded-pill">{entry}</CBadge>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <span className="dashboard-summary-label">Fehlende Verifikationen</span>
              {trustSummary.missingVerifications.length ? (
                <ul className="mb-0 mt-2 pl-4 text-sm text-slate-700">
                  {trustSummary.missingVerifications.map((entry) => <li key={entry}>{entry}</li>)}
                </ul>
              ) : (
                <p className="mb-0 mt-2 text-sm text-slate-600">Keine offenen Trust-Verifikationen.</p>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <span className="dashboard-summary-label">Fraud- / Misrepresentation-Flags</span>
              {trustSummary.flags.length ? (
                <div className="mt-2 d-flex flex-wrap gap-2">
                  {trustSummary.flags.map((entry) => (
                    <CBadge key={entry} color="danger" shape="rounded-pill">{entry}</CBadge>
                  ))}
                </div>
              ) : (
                <p className="mb-0 mt-2 text-sm text-slate-600">Keine aktiven Flags.</p>
              )}
            </div>
          </aside>
        </div>
      </section>
      <section className="dashboard-card-shell space-y-4">
        <div className="grid gap-4 xl:grid-cols-2">
          <div>
            <span className="dashboard-kicker">Trust Signals</span>
            <h2 className="dashboard-card-title">Kontakt-, Domain- und Einrichtungspruefung</h2>
          </div>
          <div className="text-sm text-slate-600">
            Die Signale fuehren hart in Status, Flags und Publishing-Blocker zurueck. Freemail, Domain-Mismatch und fehlende Veroeffentlichungsbefugnis bleiben nicht mehr nur Hinweise.
          </div>
        </div>
        <CSmartTable
          columns={[
            { key: "label", label: "Signal" },
            { key: "ok", label: "Status" },
          ]}
          items={trustSignalRows}
          pagination={false}
          sorter={false}
          tableProps={{ hover: true, responsive: true }}
          scopedColumns={{
            ok: (item) => (
              <td>
                <CBadge color={item.ok ? "success" : "danger"}>{item.ok ? "ok" : "offen / unklar"}</CBadge>
              </td>
            ),
          }}
        />
      </section>

      <section className="dashboard-card-shell space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="dashboard-kicker">Adaptive Trust</span>
            <h2 className="dashboard-card-title">Adaptive Evidence Packs und GPT Reality Check</h2>
            <p className="dashboard-section-copy">Institutionelle Arbeitgeber, kleine Betriebe und Dienstleister laufen nicht mehr durch dieselbe Einheitspruefung. Der Reality Check liefert externe Signale, entscheidet aber nicht allein.</p>
          </div>
          {isReviewer ? (
            <CLoadingButton color="primary" loading={realityChecking} onClick={runRealityCheck}>
              Reality Check jetzt ausfuehren
            </CLoadingButton>
          ) : null}
        </div>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="mb-3 d-flex justify-content-between gap-3">
              <div>
                <span className="dashboard-kicker">Evidence Pack</span>
                <h3 className="dashboard-card-title mb-0">Pflichten nach Employer-Typ</h3>
              </div>
              <CBadge color={getTrustTierColor(trustOpsSummary.trustTier)}>{trustOpsSummary.employerClassificationLabel}</CBadge>
            </div>
            <CSmartTable
              columns={[
                { key: "label", label: "Nachweis" },
                { key: "required", label: "Pflicht" },
                { key: "status", label: "Status" },
                { key: "note", label: "Hinweis" },
              ]}
              items={trustEvidenceRows}
              pagination={false}
              sorter={false}
              tableProps={{ hover: true, responsive: true }}
              scopedColumns={{
                status: (item) => (
                  <td>
                    <CBadge color={item.statusColor}>{item.status}</CBadge>
                  </td>
                ),
              }}
            />
          </div>

          <div className="d-grid gap-3">
            <CCallout color={trustOpsSummary.realityCheck.requiresReview || trustOpsSummary.riskLevel === "high" || trustOpsSummary.riskLevel === "critical" ? "warning" : "info"}>
              <strong>Reality-Check-Einschaetzung</strong>
              <div className="mt-1">{trustOpsSummary.realityCheck.summary || "Noch kein externer Reality Check vorhanden."}</div>
              <div className="mt-2 text-sm">
                Empfehlung: {trustOpsSummary.realityCheck.recommendation || "Noch keine Empfehlung berechnet."}
              </div>
            </CCallout>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="dashboard-summary-label">Risikobild</span>
              <div className="mt-2 d-flex flex-wrap gap-2">
                <CBadge color={getRiskLevelColor(trustOpsSummary.riskLevel)}>{trustOpsSummary.riskLevelLabel}</CBadge>
                <CBadge color={getPublishingRightsStageColor(trustOpsSummary.publishingRightsStage)}>{trustOpsSummary.publishingRightsStageLabel}</CBadge>
                {trustOpsSummary.suspiciousActivityFlag ? <CBadge color="danger">Suspicious activity</CBadge> : null}
                {trustOpsSummary.realityCheck.requiresReview ? <CBadge color="warning">Review noetig</CBadge> : null}
              </div>
              <p className="mb-0 mt-2 text-sm text-slate-600">
                Plausibilitaet {(Number(trustOpsSummary.realityCheck.plausibilityScore || 0) * 100).toFixed(0)}% · GPT-Risiko {(Number(trustOpsSummary.realityCheck.riskScore || 0) * 100).toFixed(0)}% · Behavior {(trustOpsSummary.behaviorRiskScore * 100).toFixed(0)}%
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="dashboard-summary-label">Reality Flags</span>
              {trustOpsSummary.realityCheck.flags?.length ? (
                <div className="mt-2 d-flex flex-wrap gap-2">
                  {trustOpsSummary.realityCheck.flags.map((entry) => (
                    <CBadge key={entry} color="warning" shape="rounded-pill">{entry}</CBadge>
                  ))}
                </div>
              ) : (
                <p className="mb-0 mt-2 text-sm text-slate-600">Keine aktiven Reality-Check-Flags.</p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="dashboard-card-shell space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="dashboard-kicker">Trust Operations</span>
            <h2 className="dashboard-card-title">Review, Tasks und Incidents steuern den Trust-Prozess</h2>
            <p className="dashboard-section-copy">Trust ist jetzt ein echter Betriebsprozess: Review-Workflow, blockierende Tasks, Incidents und naechste Aktion laufen gemeinsam in dieselbe Arbeitgeberakte.</p>
          </div>
          <CCallout color={getTrustReviewWorkflowStatusColor(trustOpsSummary.reviewWorkflowStatus)} className="mb-0 py-2 px-3">
            <strong>{trustOpsSummary.reviewWorkflowLabel}</strong>
            <div>{trustOpsSummary.nextAction}</div>
          </CCallout>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Review Tasks</span>
            <strong>{trustOpsSummary.blockingTasks}</strong>
            <p className="mb-0 text-sm text-slate-600">blockierend · {trustOpsSummary.openTasks} offen</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Incidents</span>
            <strong>{trustOpsSummary.activeIncidents}</strong>
            <p className="mb-0 text-sm text-slate-600">{trustOpsSummary.criticalIncidents} kritisch aktiv</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Investigation</span>
            <strong>{trustOpsSummary.underInvestigation ? "aktiv" : "nein"}</strong>
            <p className="mb-0 text-sm text-slate-600">Under-Investigation-Lage fuer Publishing und Sichtbarkeit.</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <span className="dashboard-summary-label">Re-Verification</span>
            <strong>{trustSummary.trustRecheckRequired ? "faellig" : "sauber"}</strong>
            <p className="mb-0 text-sm text-slate-600">
              Letztverifiziert: {formatStamp(trustSummary.trustLastVerifiedAt)} · naechster Recheck: {formatStamp(trustSummary.trustRecheckDueAt)}
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-3 d-flex justify-content-between gap-3">
            <div>
              <span className="dashboard-kicker">Review Areas</span>
              <h3 className="dashboard-card-title mb-0">Jeder Trust-Bereich hat einen eigenen Review-Status</h3>
            </div>
          </div>
          <CSmartTable
            columns={[
              { key: "areaLabel", label: "Bereich" },
              { key: "reviewStatusLabel", label: "Status" },
              { key: "reviewNotes", label: "Notiz" },
              { key: "reviewedBy", label: "Reviewed by" },
              { key: "reviewedAt", label: "Reviewed at" },
              { key: "actions", label: "Aktion", sorter: false, filter: false },
            ]}
            items={trustReviewRows}
            pagination={false}
            sorter={false}
            tableProps={{ hover: true, responsive: true }}
            scopedColumns={{
              reviewStatusLabel: (item) => (
                <td>
                  <CBadge color={item.reviewStatusColor}>{item.reviewStatusLabel}</CBadge>
                </td>
              ),
              reviewNotes: (item) => <td>{item.reviewNotes || "-"}</td>,
              reviewedAt: (item) => <td>{formatStamp(item.reviewedAt)}</td>,
              actions: (item) => (
                <td>
                  {isReviewer ? (
                    <CButton
                      color="secondary"
                      size="sm"
                      variant="outline"
                      onClick={() => setReviewDraft({
                        areaKey: item.areaKey,
                        areaLabel: item.areaLabel,
                        reviewStatus: item.reviewStatus,
                        reviewNotes: item.reviewNotes,
                        reason: "",
                      })}
                    >
                      Review
                    </CButton>
                  ) : "Read only"}
                </td>
              ),
            }}
          />
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-3 d-flex justify-content-between gap-3">
            <div>
              <span className="dashboard-kicker">Review Tasks</span>
              <h3 className="dashboard-card-title mb-0">Task-getriebener Trust Review statt Freitext</h3>
            </div>
          </div>
          <CSmartTable
            columns={[
              { key: "title", label: "Task" },
              { key: "statusLabel", label: "Status" },
              { key: "blocking", label: "Blockiert" },
              { key: "nextAction", label: "Naechste Aktion" },
              { key: "reviewedBy", label: "Reviewed by" },
              { key: "actions", label: "Aktion", sorter: false, filter: false },
            ]}
            items={trustTaskRows}
            pagination={false}
            sorter={false}
            tableProps={{ hover: true, responsive: true }}
            scopedColumns={{
              statusLabel: (item) => (
                <td>
                  <CBadge color={item.statusColor}>{item.statusLabel}</CBadge>
                </td>
              ),
              actions: (item) => (
                <td>
                  {isReviewer ? (
                    <CButton
                      color="secondary"
                      size="sm"
                      variant="outline"
                      onClick={() => setTaskDraft({
                        id: item.id,
                        title: item.title,
                        status: item.status,
                        isBlocking: item.blocking === "ja",
                        nextAction: item.nextAction === "-" ? "" : item.nextAction,
                        notes: item.notes,
                        reason: "",
                      })}
                    >
                      Task steuern
                    </CButton>
                  ) : "Read only"}
                </td>
              ),
            }}
          />
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-3 d-flex justify-content-between gap-3">
            <div>
              <span className="dashboard-kicker">Incidents</span>
              <h3 className="dashboard-card-title mb-0">Beschwerden werden zu echten Trust-Incidents</h3>
            </div>
            {isReviewer ? (
              <CButton
                color="primary"
                variant="outline"
                onClick={() => setIncidentDraft({
                  id: null,
                  type: "other",
                  severity: "medium",
                  status: "open",
                  summary: "",
                  actionTaken: "",
                  reportedBy: getStoredIdentity().userid || "admin",
                  reason: "",
                })}
              >
                Incident anlegen
              </CButton>
            ) : null}
          </div>
          <CSmartTable
            columns={[
              { key: "typeLabel", label: "Incident" },
              { key: "severity", label: "Severity" },
              { key: "statusLabel", label: "Status" },
              { key: "reportedBy", label: "Reported by" },
              { key: "summary", label: "Summary" },
              { key: "actions", label: "Aktion", sorter: false, filter: false },
            ]}
            items={trustIncidentRows}
            pagination={false}
            sorter={false}
            tableProps={{ hover: true, responsive: true }}
            scopedColumns={{
              statusLabel: (item) => (
                <td>
                  <CBadge color={item.statusColor}>{item.statusLabel}</CBadge>
                </td>
              ),
              actions: (item) => (
                <td>
                  {isReviewer ? (
                    <CButton
                      color="secondary"
                      size="sm"
                      variant="outline"
                      onClick={() => setIncidentDraft({
                        id: item.id,
                        type: item.type,
                        severity: item.severity,
                        status: item.status,
                        summary: item.summary,
                        actionTaken: item.actionTaken,
                        reportedBy: item.reportedBy,
                        reason: "",
                      })}
                    >
                      Incident bearbeiten
                    </CButton>
                  ) : "Read only"}
                </td>
              ),
            }}
          />
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <span className="dashboard-kicker">Trust History</span>
          <h3 className="dashboard-card-title">Jede Trust-Aenderung bleibt nachvollziehbar</h3>
          <CSmartTable
            columns={[
              { key: "eventType", label: "Event" },
              { key: "reason", label: "Grund" },
              { key: "changedBy", label: "Geaendert von" },
              { key: "changedAt", label: "Zeit" },
            ]}
            items={trustHistoryRows}
            pagination={false}
            sorter={false}
            tableProps={{ hover: true, responsive: true }}
            scopedColumns={{
              changedAt: (item) => <td>{formatStamp(item.changedAt)}</td>,
            }}
          />
        </div>
      </section>

      <CModal visible={showConfirm} onClose={() => setShowConfirm(false)}>
        <CModalHeader>
          <CModalTitle>Employer Gate speichern</CModalTitle>
        </CModalHeader>
        <CModalBody>
          Employer Gate, Trust-Daten, Verifikationsstand und operative Arrival-Angaben werden gemeinsam gespeichert und steuern kuenftig Profil, Jobs, Compliance und Analytics aus derselben Wahrheit.
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setShowConfirm(false)}>
            Abbrechen
          </CButton>
          <CLoadingButton color="primary" loading={saving} onClick={saveProfile}>
            Profil speichern
          </CLoadingButton>
        </CModalFooter>
      </CModal>

      <CModal visible={Boolean(reviewDraft)} onClose={() => setReviewDraft(null)}>
        <CModalHeader>
          <CModalTitle>Review-Bereich steuern</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {reviewDraft ? (
            <div className="d-grid gap-3">
              <div>
                <CFormLabel>Bereich</CFormLabel>
                <CFormInput value={reviewDraft.areaLabel} disabled />
              </div>
              <div>
                <CFormLabel>Status</CFormLabel>
                <CFormSelect value={reviewDraft.reviewStatus} options={reviewWorkflowOptions} onChange={(event) => setReviewDraft((current) => ({ ...current, reviewStatus: event.target.value }))} />
              </div>
              <div>
                <CFormLabel>Review-Notiz</CFormLabel>
                <CFormTextarea rows={5} value={reviewDraft.reviewNotes} onChange={(event) => setReviewDraft((current) => ({ ...current, reviewNotes: event.target.value }))} />
              </div>
              <div>
                <CFormLabel>Grund</CFormLabel>
                <CFormInput value={reviewDraft.reason} onChange={(event) => setReviewDraft((current) => ({ ...current, reason: event.target.value }))} />
              </div>
            </div>
          ) : null}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setReviewDraft(null)}>
            Abbrechen
          </CButton>
          <CLoadingButton color="primary" loading={opsSaving} onClick={saveReviewArea}>
            Review speichern
          </CLoadingButton>
        </CModalFooter>
      </CModal>

      <CModal visible={Boolean(taskDraft)} onClose={() => setTaskDraft(null)}>
        <CModalHeader>
          <CModalTitle>Review-Task steuern</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {taskDraft ? (
            <div className="d-grid gap-3">
              <div>
                <CFormLabel>Task</CFormLabel>
                <CFormInput value={taskDraft.title} disabled />
              </div>
              <div>
                <CFormLabel>Status</CFormLabel>
                <CFormSelect value={taskDraft.status} options={trustTaskStatusOptions} onChange={(event) => setTaskDraft((current) => ({ ...current, status: event.target.value }))} />
              </div>
              <CFormCheck checked={taskDraft.isBlocking} label="Task blockiert Publishing" onChange={(event) => setTaskDraft((current) => ({ ...current, isBlocking: event.target.checked }))} />
              <div>
                <CFormLabel>Naechste Aktion</CFormLabel>
                <CFormInput value={taskDraft.nextAction} onChange={(event) => setTaskDraft((current) => ({ ...current, nextAction: event.target.value }))} />
              </div>
              <div>
                <CFormLabel>Notiz</CFormLabel>
                <CFormTextarea rows={5} value={taskDraft.notes} onChange={(event) => setTaskDraft((current) => ({ ...current, notes: event.target.value }))} />
              </div>
              <div>
                <CFormLabel>Grund</CFormLabel>
                <CFormInput value={taskDraft.reason} onChange={(event) => setTaskDraft((current) => ({ ...current, reason: event.target.value }))} />
              </div>
            </div>
          ) : null}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setTaskDraft(null)}>
            Abbrechen
          </CButton>
          <CLoadingButton color="primary" loading={opsSaving} onClick={saveReviewTask}>
            Task speichern
          </CLoadingButton>
        </CModalFooter>
      </CModal>

      <CModal visible={Boolean(incidentDraft)} onClose={() => setIncidentDraft(null)}>
        <CModalHeader>
          <CModalTitle>{incidentDraft?.id ? "Incident aktualisieren" : "Incident anlegen"}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {incidentDraft ? (
            <div className="d-grid gap-3">
              <div>
                <CFormLabel>Incident-Typ</CFormLabel>
                <CFormSelect value={incidentDraft.type} options={incidentTypeOptions} onChange={(event) => setIncidentDraft((current) => ({ ...current, type: event.target.value }))} />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <CFormLabel>Severity</CFormLabel>
                  <CFormSelect value={incidentDraft.severity} options={incidentSeverityOptions} onChange={(event) => setIncidentDraft((current) => ({ ...current, severity: event.target.value }))} />
                </div>
                <div>
                  <CFormLabel>Status</CFormLabel>
                  <CFormSelect value={incidentDraft.status} options={incidentStatusOptions} onChange={(event) => setIncidentDraft((current) => ({ ...current, status: event.target.value }))} />
                </div>
              </div>
              <div>
                <CFormLabel>Reported by</CFormLabel>
                <CFormInput value={incidentDraft.reportedBy} onChange={(event) => setIncidentDraft((current) => ({ ...current, reportedBy: event.target.value }))} />
              </div>
              <div>
                <CFormLabel>Summary</CFormLabel>
                <CFormTextarea rows={5} value={incidentDraft.summary} onChange={(event) => setIncidentDraft((current) => ({ ...current, summary: event.target.value }))} />
              </div>
              <div>
                <CFormLabel>Action taken</CFormLabel>
                <CFormTextarea rows={4} value={incidentDraft.actionTaken} onChange={(event) => setIncidentDraft((current) => ({ ...current, actionTaken: event.target.value }))} />
              </div>
              <div>
                <CFormLabel>Grund</CFormLabel>
                <CFormInput value={incidentDraft.reason} onChange={(event) => setIncidentDraft((current) => ({ ...current, reason: event.target.value }))} />
              </div>
            </div>
          ) : null}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setIncidentDraft(null)}>
            Abbrechen
          </CButton>
          <CLoadingButton color="primary" loading={opsSaving} onClick={saveIncident}>
            Incident speichern
          </CLoadingButton>
        </CModalFooter>
      </CModal>

      <COffcanvas placement="end" visible={showTrustDetails} onHide={() => setShowTrustDetails(false)}>
        <COffcanvasHeader closeButton>
          <COffcanvasTitle>Trust-Details</COffcanvasTitle>
        </COffcanvasHeader>
        <COffcanvasBody>
          <div className="d-grid gap-3">
            <CCallout color={getEmployerTrustStatusColor(trustSummary.trustStatus)}>
              <strong>{trustSummary.trustStatusLabel}</strong>
              <div>{trustSummary.nextTrustAction}</div>
            </CCallout>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="dashboard-summary-label">Employer-Typ</span>
              <strong>{trustSummary.employerTypeLabel}</strong>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="dashboard-summary-label">Adaptive Lage</span>
              <div className="mt-2 d-flex flex-wrap gap-2">
                <CBadge color={getTrustTierColor(trustOpsSummary.trustTier)}>{trustOpsSummary.trustTierLabel}</CBadge>
                <CBadge color={getRiskLevelColor(trustOpsSummary.riskLevel)}>{trustOpsSummary.riskLevelLabel}</CBadge>
                <CBadge color={getPublishingRightsStageColor(trustOpsSummary.publishingRightsStage)}>{trustOpsSummary.publishingRightsStageLabel}</CBadge>
              </div>
              <p className="mb-0 mt-2 text-sm text-slate-700">
                Klasse: {trustOpsSummary.employerClassificationLabel} · Behavior {(trustOpsSummary.behaviorRiskScore * 100).toFixed(0)}%
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="dashboard-summary-label">Publish Blocker</span>
              {trustBlockerRows.length ? (
                <ul className="mb-0 mt-2 pl-4 text-sm text-slate-700">
                  {trustBlockerRows.map((entry) => <li key={entry.id}>{entry.blocker}</li>)}
                </ul>
              ) : (
                <p className="mb-0 mt-2 text-sm text-slate-600">Keine aktiven Publish-Blocker.</p>
              )}
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="dashboard-summary-label">Beschwerden und Recheck</span>
              <p className="mb-0 mt-2 text-sm text-slate-700">
                Beschwerden: {trustSummary.complaintCount} · Active Flag: {trustSummary.activeComplaintFlag ? "ja" : "nein"} · Recheck: {trustSummary.trustRecheckRequired ? "ja" : "nein"}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="dashboard-summary-label">Operations-Lage</span>
              <p className="mb-0 mt-2 text-sm text-slate-700">
                Workflow: {trustOpsSummary.reviewWorkflowLabel} · Blocking Tasks: {trustOpsSummary.blockingTasks} · Aktive Incidents: {trustOpsSummary.activeIncidents} · Investigation: {trustOpsSummary.underInvestigation ? "ja" : "nein"}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <span className="dashboard-summary-label">Reality Check</span>
              <p className="mb-0 mt-2 text-sm text-slate-700">
                {trustOpsSummary.realityCheck.summary || "Noch kein externer Reality Check vorhanden."}
              </p>
              <p className="mb-0 mt-2 text-sm text-slate-600">
                Plausibilitaet {(Number(trustOpsSummary.realityCheck.plausibilityScore || 0) * 100).toFixed(0)}% · Risiko {(Number(trustOpsSummary.realityCheck.riskScore || 0) * 100).toFixed(0)}% · letzter Lauf {formatStamp(trustOpsSummary.realityCheck.lastCheckedAt)}
              </p>
            </div>
          </div>
        </COffcanvasBody>
      </COffcanvas>
    </EmployerPageFrame>
  );
}
